/**
 * Progress Billing data: Project P&L rows + QuickBooks invoices + base sales
 * orders → cost-to-cost WIP projects, totals, and report tables.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import type { QBInvoice, SalesOrder } from '../types';
import {
  buildProgressBillingProjects,
  summarizeProgressBilling,
  type ProgressBillingProject,
  type ProgressBillingTotals,
} from '../utils/progressBilling';
import { useProjectPLRows } from './useProjectPLRows';
import { useProjectPLWorkspace } from './useProjectPLWorkspace';

export interface UseProgressBillingDataResult {
  canAccess: boolean;
  ready: boolean;
  projects: ProgressBillingProject[];
  wip: ProgressBillingProject[];
  completed: ProgressBillingProject[];
  totals: ProgressBillingTotals;
  /** Any P&L row has a proposal budget — drives the empty state. */
  hasBudgets: boolean;
  syncing: boolean;
  lastSyncAt: Date | null;
  triggerSync: () => Promise<void> | void;
}

export function useProgressBillingData(asOf: Date): UseProgressBillingDataResult {
  const workspace = useProjectPLWorkspace();
  const { rows, groupByDealId, ready: rowsReady } = useProjectPLRows(workspace);
  const { canAccess } = workspace;

  const [qbInvoices, setQbInvoices] = useState<Array<QBInvoice & { id: string }>>([]);
  const [salesOrders, setSalesOrders] = useState<Array<SalesOrder & { id: string }>>([]);
  const [invoicesReady, setInvoicesReady] = useState(false);
  const [ordersReady, setOrdersReady] = useState(false);

  useEffect(() => {
    if (!canAccess) return;
    const unsub = onSnapshot(
      collection(db, 'qbInvoices'),
      snap => {
        setQbInvoices(snap.docs.map(d => ({ id: d.id, ...(d.data() as QBInvoice) })));
        setInvoicesReady(true);
      },
      err => {
        console.error('qbInvoices subscription failed:', err);
        setInvoicesReady(true);
      },
    );
    return () => unsub();
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) return;
    const unsub = onSnapshot(
      collection(db, 'salesOrders'),
      snap => {
        setSalesOrders(snap.docs.map(d => ({ id: d.id, ...(d.data() as SalesOrder) })));
        setOrdersReady(true);
      },
      err => {
        console.error('salesOrders subscription failed:', err);
        setOrdersReady(true);
      },
    );
    return () => unsub();
  }, [canAccess]);

  const asOfKey = asOf.getTime();
  const plRows = useMemo(
    () => rows.map(r => ({ ...r, group: groupByDealId.get(r.dealId) ?? 'contracted' })),
    [rows, groupByDealId],
  );
  const projects = useMemo(
    () =>
      buildProgressBillingProjects({
        plRows,
        qbInvoices,
        salesOrders,
        asOf: new Date(asOfKey),
      }),
    [plRows, qbInvoices, salesOrders, asOfKey],
  );

  const totals = useMemo(() => summarizeProgressBilling(projects, new Date(asOfKey)), [projects, asOfKey]);
  const wip = useMemo(
    () => projects.filter(p => p.status === 'wip').sort((a, b) => b.contractValue - a.contractValue),
    [projects],
  );
  const completed = useMemo(
    () =>
      projects
        .filter(p => p.status === 'completed')
        .sort((a, b) => (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0)),
    [projects],
  );
  const hasBudgets = useMemo(() => rows.some(r => r.budget > 0), [rows]);

  return {
    canAccess,
    ready: canAccess && rowsReady && invoicesReady && ordersReady,
    projects,
    wip,
    completed,
    totals,
    hasBudgets,
    syncing: workspace.syncing,
    lastSyncAt: workspace.lastSyncAt,
    triggerSync: workspace.triggerSync,
  };
}
